import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { enumCompanyBusinessType } from '@app/data-layer/enums/companies';
import { enumShippingRole, enumUserBetaFlag } from '@app/data-layer/enums/user';
import { CompanyModel } from '@data-layer/models/companies';
import { ActivatedRoute, Router } from '@angular/router';
import { CompaniesService } from '@services/companies.service';
import { AlertService } from '@services/alert.service';
import { enumCompanyPlacement } from '@enum/enumCompanyPlacement';
import { UntypedFormGroup } from '@angular/forms';
import { IHeaderItem } from '@app/modules/app-layout/interfaces';
import { AppLayoutService } from '@app/modules/app-layout/app-layout.service';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { GenericService } from '@services/generic.service';
import { BillsRepository, CompaniesRepository } from '@app/data-layer/repositories';
import { enumPaymentProvider } from '@app/data-layer/enums/payments';
import { OrganizationModel } from '@app/data-layer/models/organizations';
import { UserBetaFlagModel, UserModel } from '@app/data-layer/models/user';
import { OrganizationsService } from '@services/organizations.service';

@Component({
  selector: 'account-settings-company-detailed',
  templateUrl: './account-settings-company-detailed.component.html',
  styleUrls: ['./account-settings-company-detailed.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSettingsCompanyDetailedComponent implements OnInit, OnDestroy {
  public formGroup: UntypedFormGroup;
  //TODO: remove company varialbe and logic after new bonds
  public organization: OrganizationModel;
  public company: CompanyModel;
  public currentUser: UserModel;
  public isTablet: boolean;

  public enumShippingRole: typeof enumShippingRole = enumShippingRole;
  public enumCompanyBusinessType: typeof enumCompanyBusinessType = enumCompanyBusinessType;
  public isPayPalPaymentTokenLoading: boolean;
  public isDeletingCompany: boolean = false;
  public isDeleteModalOpen: boolean = false;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private organizationsService: OrganizationsService,
    private companiesService: CompaniesService,
    private appLayoutService: AppLayoutService,
    private genericService: GenericService,
    private billsRepository: BillsRepository,
    private companiesRepository: CompaniesRepository,
    private alertService: AlertService,
    private router: Router,
  ) {}

  public ngOnInit(): void {
    this.genericService.isTablet$.pipe(takeUntil(this.destroy$)).subscribe((state: boolean) => {
      this.isTablet = state;
    });
    this.createForm();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public revertFormToInitialState(): void {
    this.createForm(false);
  }

  public onRemoveMonthlyPayment(currentPaymentMethodProvider: enumPaymentProvider): void {
    let deletePaymentProviderMethod: Observable<CompanyModel | void>;
    switch (currentPaymentMethodProvider) {
      case enumPaymentProvider.STRIPE:
        deletePaymentProviderMethod = this.companiesService.deleteDefaultPaymentMethod(this.company.id, true);
        break;
      case enumPaymentProvider.PAYPAL:
        deletePaymentProviderMethod = this.paypalPaymentRemove(this.company.id);
        break;
      default:
        deletePaymentProviderMethod = of();
        break;
    }

    const combinedObservers$ = combineLatest([this.getCompany(), this.getCompanies()]);

    deletePaymentProviderMethod
      .pipe(
        switchMap(() => combinedObservers$),
        takeUntil(this.destroy$),
      )
      .subscribe(([company, _]) => {
        this.company = company;
        this.companiesService.setDefaultCompany(this.company);
        this.cdr.markForCheck();
      });
  }

  public onPaypalPaymentToken(): void {
    const params = this.route.snapshot.queryParams;
    const isApprovalTokenId = params.approval_token_id;

    if (this.isWithPaypalTokenParams) {
      this.isPayPalPaymentTokenLoading = true;

      const combinedObservers$ = combineLatest([this.getCompany(), this.getCompanies()]);

      this.paypalPaymentToken(this.company.id, isApprovalTokenId)
        .pipe(
          switchMap(() => combinedObservers$),
          takeUntil(this.destroy$),
        )
        .subscribe(([company, _]) => {
          this.removeQueryParams(['isApproved', 'approval_token_id', 'approval_session_id']);
          this.company = company;
          this.companiesService.setDefaultCompany(this.company);
          this.isPayPalPaymentTokenLoading = false;
          this.cdr.markForCheck();
        });
    }
  }

  public onShowDeleteConfirm(): void {
    this.isDeleteModalOpen = true;
  }

  public onHideDeleteConfirm(): void {
    if (this.isDeletingCompany) {
      return;
    }
    this.isDeleteModalOpen = false;
  }

  public onDeleteCompany(): void {
    if (this.isDeletingCompany) {
      return;
    }
    this.isDeletingCompany = true;
    this.cdr.markForCheck();
    this.companiesRepository
      .deleteCompany(this.company.id)
      .pipe(
        finalize(() => {
          this.isDeletingCompany = false;
          this.isDeleteModalOpen = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          const deletedId: number = this.company.id;
          this.companiesService.syncAfterCompanyDeleted(deletedId).pipe(takeUntil(this.destroy$)).subscribe();
          this.router.navigate(['/companies', 'account-settings', 'company-list']);
        },
        error: () => {
          this.alertService.error();
        },
      });
  }

  public onCompanyUpdated(): void {
    this.getCompany()
      .pipe(takeUntil(this.destroy$))
      .subscribe((company: CompanyModel) => {
        this.company = company;
        this.companiesService.setDefaultCompany(this.company);
        this.cdr.markForCheck();
      });
  }

  private get isWithPaypalTokenParams(): boolean {
    const params = this.route.snapshot.queryParams;
    const isApprovedReturnUrl = params.isApproved;
    const isApprovalTokenId = params.approval_token_id;
    return !!isApprovedReturnUrl && !!isApprovalTokenId;
  }

  private paypalPaymentToken(companyId: number, approvalSessionId: string): Observable<void> {
    return this.billsRepository.paypalPaymentToken(companyId, approvalSessionId);
  }

  private paypalPaymentRemove(companyId: number): Observable<void> {
    return this.billsRepository.paypalPaymentRemove(companyId);
  }

  private removeQueryParams(paramsToRemove: string[]) {
    const queryParams = { ...this.route.snapshot.queryParams };

    paramsToRemove.forEach((param) => {
      delete queryParams[param];
    });

    this.router.navigate([], { queryParams });
  }

  private createForm(isUpdateHeader: boolean = true): void {
    this.getCompany()
      .pipe(takeUntil(this.destroy$))
      .subscribe((company: CompanyModel) => {
        //TODO: remove company varialbe,setting default company and logic after new bonds
        this.company = company;
        if (!this.isWithPaypalTokenParams) {
          this.companiesService.setDefaultCompany(this.company);
        }

        this.formGroup = this.companiesService.createCompanyForm(enumCompanyPlacement.COMPANY_PAGE, company);
        this.formGroup.get('isUnitedStates').patchValue(this.isHasOnboardingRedesign || company.isUnitedStates);

        if (isUpdateHeader) {
          this.updateDashboardHeader(company.businessName);
        }
        this.cdr.markForCheck();

        const firstCompany = this.companiesService.getCompanies()[0];
        this.currentUser = this.companiesService.getUser();
        this.organizationsService
          .getOrganizationByIdFromServer(this.currentUser.defaultOrganizationId || firstCompany?.organizationId)
          .pipe(takeUntil(this.destroy$))
          .subscribe((organization) => {
            this.organizationsService.setDefaultOrganization(organization);
            this.organization = organization;
            this.cdr.markForCheck();
          });
      });
  }

  private getCompany(): Observable<CompanyModel> {
    return this.companiesService.getCompanyByIdFromServer(this.route.snapshot.params.id);
  }

  private getCompanies(): Observable<CompanyModel[]> {
    return this.companiesService.getCompaniesFromServer();
  }

  private updateDashboardHeader(businessName: string): void {
    const title: IHeaderItem[] = [];
    if (this.isTablet) {
      title.push(
        {
          key: 'MENU.ACCOUNT_SETTINGS',
          link: '/companies/account-settings',
        },
        {
          key: 'LABELS.IMPORTER_OF_RECORD',
          link: null,
        },
      );
      this.appLayoutService.contentHeader$.next({ title, mobileTitle: title });
      return;
    }

    title.push({
      key: 'MENU.ACCOUNT_SETTINGS',
      link: '/companies/account-settings',
    });
    if (this.companiesService.getCompanies().length > 1) {
      title.push(
        {
          key: 'LABELS.IMPORTERS_OF_RECORD',
          link: '/companies/account-settings/company-list',
        },
        {
          key: !!businessName ? businessName : 'LABELS.IMPORTER_OF_RECORD',
          link: null,
          params: {
            intranslatable: !!businessName,
          },
        },
      );
    }
    this.appLayoutService.contentHeader$.next({ title, mobileTitle: title });
  }

  private get isHasOnboardingRedesign(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.onboardingRedesign);
  }
}
